import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Header from "../Header/Header";
import reduxStore from "../redux";
import { styles } from "../MainPage/MainPage";
import { messages } from "../common/notification";
import { useStoreListRefresh } from "../common/useStoreList";
import { setDeviceSchedule } from "../redux/action/deviceSchedule";
import {
  loadDeviceDetails,
  loadHouseForPicker,
  reloadDeviceDetails,
  updateDeviceSchedule,
} from "./api";
import { SCHEDULE_SECTIONS } from "./constants";
import {
  collectDevices,
  isPending,
  scheduledDevices,
  matchesSearch,
  mergeExpected,
  scheduleOf,
  sectionSides,
  statusOf,
  connectionOf,
  filterValues,
  liveConnectionOf,
} from "./function";
import DeviceScheduleTable from "./components/DeviceScheduleTable";
import PropertySelect from "./components/PropertySelect";
import ScheduleEditModal from "./components/ScheduleEditModal";
import { RefreshIcon, SearchIcon } from "./components/Icons";
import { usePendingAutoCheck } from "./usePendingAutoCheck";
import { useConnectionStatuses } from "./useConnectionStatuses";
import { filterOptions, matchesColumnFilters } from "../common/columnFilters";
import "./DeviceSchedule.css";

// Devices the property did not place in an area still have to land somewhere,
// and that band sorts last.
const NO_AREA = "No area";

const cellKey = (deviceId, sectionKey) => `${deviceId}:${sectionKey}`;

// The header filters, in column order: typed for the device id and name,
// picked from a list for the status and each section's Sync column.
const FILTER_SPECS = [
  { field: "deviceId", kind: "text", label: "Device ID" },
  { field: "connection", kind: "select", label: "Status" },
  { field: "name", kind: "text", label: "Name" },
  ...SCHEDULE_SECTIONS.map((section) => ({
    field: `sync:${section.key}`,
    kind: "select",
    label: `${section.label} Sync`,
  })),
];

function DeviceSchedule() {
  const dispatch = useDispatch();

  const housesList = useSelector((state) => state.housesList);
  const houses = useSelector((state) => state.mutipleHouse);
  const { property, overrides, details, followedHouseId } = useSelector(
    (state) => state.deviceSchedule
  );
  // The dashboard's store and appliance, so this page opens on the same store
  // and marks the appliance being looked at there.
  const dashboardHouseId = useSelector((state) => state.dropdownSelectionData?.houseId);
  const dashboardDeviceId = useSelector(
    (state) => state.dropdownSelectionData?.selectedDeviceDetails?.deviceId
  );

  const { refreshing: storesRefreshing, refresh: refreshStoreList } =
    useStoreListRefresh();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  // field -> typed text or picked value, for the header filters.
  const [columnFilters, setColumnFilters] = useState({});
  const setColumnFilter = (field, value) =>
    setColumnFilters((current) => ({ ...current, [field]: value }));

  // The cell being edited. A fresh key per open is what makes the editor reseed.
  const [target, setTarget] = useState(null);
  const [savingKey, setSavingKey] = useState("");
  const [saveError, setSaveError] = useState("");
  // deviceIds whose row Refresh is in flight. Several rows can refresh at once.
  const [refreshingIds, setRefreshingIds] = useState({});
  // Devices sent a schedule they have not applied yet are re-checked on their
  // own, so the row updates without anyone pressing its refresh.
  const { watch: watchPending, checkingIds } = usePendingAutoCheck(property?.houseAccessCode);
  // Live Online / Offline from /connectionStatus: every listed device when the
  // store loads, one device on its row refresh or when its editor opens.
  const {
    statuses: liveStatuses,
    checkOne: checkConnection,
    checkMany: checkConnections,
  } = useConnectionStatuses(property?.houseAccessCode);

  const house = property?.houseAccessCode
    ? houses?.[property.houseAccessCode] || null
    : null;

  // Two reads, and the rows need both: the property names its devices, then
  // each device document carries the schedules.
  const load = useCallback(
    async (picked, force) => {
      if (!picked) return;

      setLoading(true);
      setError("");

      const result = await loadHouseForPicker(picked, { force });

      if (!result.success) {
        setLoading(false);
        setError(result.message);
        return;
      }

      // Only the devices this page lists need their documents.
      const details = await loadDeviceDetails(
        scheduledDevices(result.data).map((entry) => entry.deviceId)
      );

      // The user may have picked another property while this was in flight;
      // its documents must not land over that one's.
      const current = reduxStore.getState().deviceSchedule?.property;
      if (current?.houseAccessCode !== picked.houseAccessCode) return;

      dispatch(setDeviceSchedule({ details: details.details }));
      setLoading(false);

      // Then each device's live connection, a few at a time. The rows show the
      // documents' status until these land.
      checkConnections(scheduledDevices(result.data).map((entry) => entry.deviceId));

      // The devices are listed either way, so a failure here is reported
      // without throwing the rows away - they just read as "Not set".
      if (!details.success) setError(details.message);
    },
    [dispatch, checkConnections]
  );

  // Per device per section: what the device reports, what the cloud is asking
  // for, and whether those two disagree.
  const rows = useMemo(() => {
    // Only devices set to be scheduled (Device Master's flag) are listed.
    return scheduledDevices(house).map((entry) => {
      const cells = {};

      // The device document wins over the assets entry: it is the fresher read
      // and the only one that carries schedules.
      const device = { ...entry.device, ...(details?.[entry.deviceId] || {}) };

      for (let section of SCHEDULE_SECTIONS) {
        // A just-sent change merges onto the expected side, so the row reads as
        // pending immediately - without a refetch, and without the table
        // claiming the device has applied anything.
        const override = overrides?.[entry.deviceId]?.[section.key] || null;
        const { current, expected } = sectionSides(device, section, override);

        cells[section.key] = {
          current,
          desired: mergeExpected(current, expected, section),
          pending: isPending(current, expected, section),
          status: statusOf(current, section),
        };
      }

      // The live answer when there is one; the document's otherwise.
      const live = liveStatuses[entry.deviceId];
      const connection = live ? liveConnectionOf(live, device) : connectionOf(device);

      return { ...entry, cells, connection };
    });
  }, [house, overrides, details, liveStatuses]);

  // Each row's filterable values, and the choices the select filters offer.
  const rowFilterValues = useMemo(
    () => new Map(rows.map((row) => [row.deviceId, filterValues(row)])),
    [rows]
  );
  const columnFilterOptions = useMemo(() => {
    const values = [...rowFilterValues.values()];
    return Object.fromEntries(
      FILTER_SPECS.filter(({ kind }) => kind === "select").map(({ field }) => [
        field,
        filterOptions(values, field),
      ])
    );
  }, [rowFilterValues]);

  // Banded by area, areas alphabetical, devices in the order the service
  // returned them.
  const groups = useMemo(() => {
    const byArea = new Map();

    for (let row of rows) {
      if (!matchesSearch(row, search)) continue;
      if (!matchesColumnFilters(rowFilterValues.get(row.deviceId), columnFilters, FILTER_SPECS)) {
        continue;
      }

      const area = row.roomName || NO_AREA;
      if (!byArea.has(area)) byArea.set(area, []);
      byArea.get(area).push(row);
    }

    return [...byArea.entries()]
      .map(([area, areaRows]) => ({ area, rows: areaRows }))
      .sort((a, b) =>
        a.area === NO_AREA
          ? 1
          : b.area === NO_AREA
            ? -1
            : a.area.localeCompare(b.area)
      );
  }, [rows, search, rowFilterValues, columnFilters]);

  const pickProperty = useCallback((picked) => {
    setError("");
    setSearch("");
    setColumnFilters({});
    // A property's overrides and device documents say nothing about another's.
    dispatch(setDeviceSchedule({ property: picked, overrides: {}, details: {} }));

    // Picking a property always reads it fresh. The multi-house slice is
    // persisted but the device documents are not, so after a browser reload -
    // or on coming back to a property picked earlier - a cached house alone
    // would list every device as "Not set" until Refresh was pressed. Coming
    // back to this tab is unaffected: the selection and its documents stay in
    // the deviceSchedule slice, and no pick happens.
    if (picked) load(picked, true);
  }, [dispatch, load]);

  // Opens on the dashboard's store when that has changed since this page last
  // followed it. Waits for the store list, since the picker needs the entry.
  useEffect(() => {
    if (!dashboardHouseId || dashboardHouseId === followedHouseId) return;
    const entry = (housesList || []).find(
      (house) => house.houseAccessCode === dashboardHouseId
    );
    if (!entry) return;

    dispatch(setDeviceSchedule({ followedHouseId: dashboardHouseId }));
    if (property?.houseAccessCode !== dashboardHouseId) pickProperty(entry);
  }, [dashboardHouseId, followedHouseId, housesList, property, dispatch, pickProperty]);

  const highlightId =
    property?.houseAccessCode && property.houseAccessCode === dashboardHouseId
      ? dashboardDeviceId
      : null;

  const refresh = () => {
    // Re-reading from the service is the one moment the local overrides stop
    // being the better answer: load() replaces the device documents, so a
    // change the device has since applied now arrives as its own current value.
    dispatch(setDeviceSchedule({ overrides: {} }));
    load(property, true);
  };

  // Re-reads one device instead of the whole property. Same idea as the page
  // Refresh, scoped to the row: that device's overrides are dropped and its
  // document replaced, so whatever it has since applied shows as current.
  const refreshDevice = async (deviceId) => {
    const propertyCode = property?.houseAccessCode;
    setRefreshingIds((ids) => ({ ...ids, [deviceId]: true }));

    // Its live connection alongside, so Status is current too.
    checkConnection(deviceId);
    const fresh = await reloadDeviceDetails(deviceId);

    setRefreshingIds(({ [deviceId]: done, ...rest }) => rest);

    // Read the slice as it is now, not as it was when the click happened:
    // other rows may have refreshed or saved meanwhile, and the user may have
    // moved to another property.
    const state = reduxStore.getState().deviceSchedule;
    if (state?.property?.houseAccessCode !== propertyCode) return;

    if (!fresh) {
      messages.error(`Could not refresh ${deviceId}`);
      return;
    }

    const { [deviceId]: dropped, ...otherOverrides } = state.overrides || {};
    dispatch(
      setDeviceSchedule({
        details: { ...state.details, [deviceId]: fresh },
        overrides: otherOverrides,
      })
    );
  };

  const openEditor = (row, section) => {
    setSaveError("");
    // So the editor's offline note goes by the device's state now.
    checkConnection(row.deviceId);
    setTarget({
      key: `${cellKey(row.deviceId, section.key)}:${Date.now()}`,
      row,
      section,
      cell: row.cells[section.key],
    });
  };

  const handleSave = async (schedule) => {
    if (!target) return;

    const { row, section } = target;

    setSavingKey(cellKey(row.deviceId, section.key));
    setSaveError("");

    const result = await updateDeviceSchedule({
      deviceId: row.deviceId,
      writeKey: section.writeKey,
      schedule,
    });

    if (!result.success) {
      // The modal stays open with the user's values intact.
      setSavingKey("");
      setSaveError(result.message);
      messages.error(result.message);
      return;
    }

    // Read the one device back before calling it done, so the row shows what the
    // service recorded rather than what was sent. The cell stays in its saving
    // state through this - it is part of the save, not a separate step.
    const fresh = await reloadDeviceDetails(row.deviceId);
    setSavingKey("");

    const reportedAfter = fresh ? scheduleOf(fresh, section.readKey) : null;

    // The device reports the values that were just sent: it has already applied
    // them, so this is not pending at all.
    const applied =
      Boolean(reportedAfter) && !isPending(reportedAfter, schedule, section);

    // The slice as it is now, not as it was when Save was pressed: an
    // automatic check or a row refresh may have landed meanwhile.
    const live = reduxStore.getState().deviceSchedule;
    dispatch(
      setDeviceSchedule({
        // The override stays as the fallback: if the read came back without the
        // change - or did not come back - the row still reads as pending.
        overrides: {
          ...live.overrides,
          [row.deviceId]: {
            ...(live.overrides?.[row.deviceId] || {}),
            [section.key]: schedule,
          },
        },
        details: fresh ? { ...live.details, [row.deviceId]: fresh } : live.details,
      })
    );

    // Not applied yet: keep checking the device until it is.
    if (!applied) watchPending(row.deviceId);

    // An offline device takes a change only once it reconnects, so say so
    // rather than suggest it is about to apply.
    const offline = fresh && connectionOf(fresh).online === false;
    messages.success(
      applied
        ? "Schedule updated: the device has applied it."
        : offline
          ? "Schedule sent. The device is offline - it will apply it when it comes back online."
          : "Schedule sent. Waiting for the device to pick it up - this page will check on its own."
    );
    setTarget(null);
  };

  const deviceCount = rows.length;
  // Devices in the property left out because they are not set to be scheduled.
  const allDeviceCount = useMemo(() => collectDevices(house).length, [house]);
  const unscheduledCount = allDeviceCount - deviceCount;
  const shownCount = groups.reduce((total, group) => total + group.rows.length, 0);

  const subtitle = () => {
    if (!property) return "Pick a property to load its EnergiSync devices";
    if (loading) return "Loading devices...";
    if (error) return error;
    if (allDeviceCount === 0) return "No EnergiSync devices in this property";
    if (deviceCount === 0) {
      return `None of the ${allDeviceCount} devices here are set to be scheduled`;
    }

    const shown = shownCount !== deviceCount ? `${shownCount} of ${deviceCount}` : `${deviceCount}`;
    const { houseName, houseAccessCode } = property;
    const store =
      houseName && houseAccessCode && houseName !== houseAccessCode
        ? `${houseAccessCode} - ${houseName}`
        : houseName || houseAccessCode;
    const hidden = unscheduledCount
      ? ` · ${unscheduledCount} not set to be scheduled`
      : "";
    return `${shown} device${deviceCount === 1 ? "" : "s"} in ${store}${hidden}`;
  };

  return (
    <div style={{ ...styles.container }}>
      <div style={{ ...styles.rightContainer }}>
        <Header />

        <div className="ds-page">
          <div className="ds-toolbar">
            <div>
              <div className="ds-title">Device Schedule</div>
              <div className="ds-subtitle">{subtitle()}</div>
            </div>

            <div className="ds-controls">
              <label className="ds-search">
                <SearchIcon />
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search id, name or area"
                  disabled={!property}
                />
              </label>

              <PropertySelect
                properties={housesList || []}
                value={property}
                onChange={pickProperty}
                isLoading={storesRefreshing}
                onMenuOpen={refreshStoreList}
              />

              <button
                type="button"
                className="ds-icon-button"
                onClick={refresh}
                disabled={!property || loading}
                title="Re-read this property from the service"
              >
                <span className={loading ? "ds-spin" : ""}>
                  <RefreshIcon />
                </span>
              </button>
            </div>
          </div>

          <DeviceScheduleTable
            groups={groups}
            loading={loading}
            hasProperty={Boolean(property)}
            hasDevices={deviceCount > 0}
            unscheduledCount={unscheduledCount}
            searching={search}
            filters={{
              specs: FILTER_SPECS,
              values: columnFilters,
              options: columnFilterOptions,
              onChange: setColumnFilter,
              onClear: () => setColumnFilters({}),
            }}
            savingKey={savingKey}
            onEdit={openEditor}
            // A row's refresh spins for an automatic check too.
            refreshingIds={{ ...refreshingIds, ...checkingIds }}
            onRefreshDevice={refreshDevice}
            highlightId={highlightId}
          />
        </div>
      </div>

      <ScheduleEditModal
        open={Boolean(target)}
        target={target}
        // The row as it is now, so a live status landing after the editor
        // opened still reaches its offline note.
        connection={
          target ? rows.find((row) => row.deviceId === target.row.deviceId)?.connection : null
        }
        saving={Boolean(savingKey)}
        error={saveError}
        onClose={() => setTarget(null)}
        onSave={handleSave}
      />
    </div>
  );
}

export default DeviceSchedule;
